/**
 * Light app shell of phage.energy (prototype): a panel on the left with the
 * text logo, the menu, the language pill, the user's email and «Вийти»; one
 * column of sections on the right. On a phone the panel is a one-line header
 * fixed at the top. Client menu — «Об'єкти»; administrator (role `operator`) —
 * «Об'єкти», «Заявки», «Ціни РДН».
 */
import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useProfile } from '../hooks/use-profile.js';
import { useT } from '../i18n/locale-context.js';
import type { MessageKey } from '../i18n/messages.js';
import { api } from '../lib/api-client.js';
import { queryClient } from '../lib/query-client.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { Logo } from './Logo.js';
import { Toast } from './Toast.js';
import { initials } from './format.js';
import { markSigningOut } from './session.js';

const ICONS = {
  objects: (
    <>
      <rect x="3" y="7" width="15" height="10" rx="2" />
      <path d="M21 10.5v3" />
      <path d="M7 10.5v3M10.5 10.5v3" />
    </>
  ),
  apps: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  prices: (
    <>
      <path d="M3 20h18" />
      <path d="M6 20v-7M11 20V6M16 20v-10M20 20v-4" />
    </>
  ),
};

interface NavItem {
  to: string;
  key: MessageKey;
  icon: keyof typeof ICONS;
  /** Pages under the item besides `to` itself. */
  also?: string[];
}

const CLIENT_NAV: NavItem[] = [{ to: '/app/sites', key: 'nav.objects', icon: 'objects' }];
const ADMIN_NAV: NavItem[] = [
  { to: '/app/admin/sites', key: 'nav.objects', icon: 'objects', also: ['/app/sites'] },
  { to: '/app/admin/applications', key: 'nav.applications', icon: 'apps' },
  { to: '/app/admin/dam-prices', key: 'nav.prices', icon: 'prices' },
];

/** Where «Об'єкти» is for this user: the administrator's list of all objects or the client's own. */
export function homePath(role: string | undefined): string {
  return role === 'operator' ? '/app/admin/sites' : '/app/sites';
}

export function AppShell({ children }: { children: ReactNode }) {
  const t = useT();
  const { data: profile } = useProfile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAdmin = profile?.role === 'operator';
  const nav = isAdmin ? ADMIN_NAV : CLIENT_NAV;

  const logout = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    // Before the request: a poll answered 401 meanwhile is not «Сесія завершилась».
    onMutate: () => markSigningOut(),
    onSettled: () => {
      navigate('/login', { replace: true });
      queryClient.clear();
    },
  });

  return (
    <div className="page">
      <div className={`shell${isAdmin ? ' is-admin' : ''}`}>
        <aside className="side">
          <Logo to={homePath(profile?.role)} />
          <nav className="nav" aria-label={t('nav.menu')}>
            {nav.map((item) => {
              const current = [item.to, ...(item.also ?? [])].some((p) => pathname.startsWith(p));
              return (
                <Link key={item.to} to={item.to} aria-current={current ? 'page' : undefined}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {ICONS[item.icon]}
                  </svg>
                  {t(item.key)}
                </Link>
              );
            })}
          </nav>
          <div className="side-foot">
            <LanguageSwitcher />
            {profile ? (
              <div className="user">
                <span className="avatar" aria-hidden="true">
                  {initials(profile.email)}
                </span>
                <span className="email">{profile.email}</span>
                <button
                  type="button"
                  className="logout"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                >
                  {t('nav.logout')}
                </button>
              </div>
            ) : null}
          </div>
        </aside>
        <main className="main">
          <Toast />
          {children}
        </main>
      </div>
    </div>
  );
}
